//React
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import IconButton from "../inputs/iconButton";
import HoverTip from "../inputs/hoverTip";
import {
	EMPTY,
	middleTruncate,
	valueLabel,
	valueHelp,
	formatValue,
	ratioBase,
	smallBase,
} from "./formatters";
import { roleToken } from "./palette";
//Assets
import copyIcon from "../../assets/svgs/copy.svg";
import chevronIcon from "../../assets/svgs/chevron-down.svg";
//CSS
import "./charts.scss";

const isBlank = (value) => value === null || value === undefined || value === "";

/**
 * A `url` cell (ASB-4377, design §7.5): monospace, middle-truncated, with a
 * copy button — and never a link. A scraped URL can be anything, and the
 * admin chooses whether to open it.
 */
export function UrlCell({ value, max, column }) {
	const [copied, setCopied] = useState(false);
	useEffect(() => {
		if (!copied) return undefined;
		const t = setTimeout(() => setCopied(false), 1500);
		return () => clearTimeout(t);
	}, [copied]);
	if (isBlank(value)) return <NullCell column={column} />;
	const text = String(value);
	const copy = (e) => {
		if (e && e.stopPropagation) e.stopPropagation();
		try {
			if (navigator.clipboard) navigator.clipboard.writeText(text);
		} catch (err) {
			// clipboard refused (insecure context): the full text is in the title
		}
		setCopied(true);
	};
	return (
		<span className="as-url">
			<code
				className="as-url__text"
				title={text}
			>
				{middleTruncate(text, max ?? 56)}
			</code>
			<IconButton
				icon={copyIcon}
				label={copied ? "Copied" : "Copy URL"}
				size="sm"
				className="as-url__copy"
				onClick={copy}
			/>
		</span>
	);
}

UrlCell.propTypes = {
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	/** characters kept before the middle is elided; default 56 */
	max: PropTypes.number,
	column: PropTypes.shape({ nullHelp: PropTypes.string }),
};

// Past this many characters the text is likely to need its third line, so the
// expand control is offered; shorter text fits the two-line clamp as is.
export const CLAMP_HINT = 120;

/**
 * A `text` cell. Long text (past CLAMP_HINT) is clamped to two lines with the
 * full text on expand; short text is never clamped, since nothing would offer
 * the rest, and stays on one line so a name does not wrap into a sliver when
 * the table is full.
 */
export function TextCell({ value, column }) {
	const [expanded, setExpanded] = useState(false);
	if (isBlank(value)) return <NullCell column={column} />;
	const text = String(value);
	const long = text.length > CLAMP_HINT;
	// A `multi` value ("silo21 (…); silo58 (…)") may wrap at its separators, so a
	// shared domain does not hold a column as wide as every silo's name in a row.
	const multi = Boolean(column?.multi) && text.includes(column.multi);
	return (
		<span
			className={[
				"as-text",
				long ? "as-text--long" : "",
				multi ? "as-text--multi" : "",
				expanded ? "as-text--expanded" : "",
			]
				.join(" ")
				.trim()}
		>
			<span
				className={["as-text__body", long && !expanded ? "as-text__body--clamped" : ""]
					.join(" ")
					.trim()}
			>
				{text}
			</span>
			{long ? (
				<IconButton
					icon={chevronIcon}
					label={expanded ? "Show less" : "Show full text"}
					size="sm"
					className={["as-text__toggle", expanded ? "as-text__toggle--open" : ""].join(" ").trim()}
					onClick={(e) => {
						if (e && e.stopPropagation) e.stopPropagation();
						setExpanded((v) => !v);
					}}
				/>
			) : null}
		</span>
	);
}

TextCell.propTypes = {
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	column: PropTypes.shape({ nullHelp: PropTypes.string, multi: PropTypes.string }),
};

/**
 * A null value (A.6 `nullHelp`): the em dash with the column's reason as its
 * tooltip ("No fetch on this path"). Null means "doesn't apply", never 0.
 */
export function NullCell({ column }) {
	const help = column?.nullHelp;
	if (!help) return EMPTY;
	return (
		<span
			className="as-cell-null"
			title={help}
			aria-label={`Not applicable: ${help}`}
		>
			{EMPTY}
		</span>
	);
}

NullCell.propTypes = {
	column: PropTypes.shape({ nullHelp: PropTypes.string }),
};

/**
 * An `enum` cell: the value as stored, or its label when the column maps one
 * (the stored value stays in the title), with its role's colour as a leading
 * dot when the column maps one, and the value's `help` (A.6) shown when the
 * label is hovered or focused — with no underline, since the same value
 * repeats down the column (one shared tooltip per table, from PanelTable).
 */
export function EnumCell({ value, column }) {
	if (isBlank(value)) return <NullCell column={column} />;
	const token = roleToken(value, column?.roles);
	const label = valueLabel(column, value);
	const help = valueHelp(column, value);
	return (
		<span
			className="as-enum"
			title={label !== String(value) ? String(value) : undefined}
		>
			{token ? (
				<i
					className="as-enum__dot"
					style={{ background: `var(${token})` }}
					aria-hidden="true"
				/>
			) : null}
			{help ? (
				<HoverTip
					content={help}
					underline={false}
					className="as-enum__label"
				>
					{label}
				</HoverTip>
			) : (
				label
			)}
		</span>
	);
}

EnumCell.propTypes = {
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
	column: PropTypes.shape({
		type: PropTypes.string,
		roles: PropTypes.object,
		labels: PropTypes.object,
		nullHelp: PropTypes.string,
	}),
};

/**
 * A `pct` cell with a `ratio`: the value formatted exactly, and when the
 * column declares `minBase` and the row's denominator is below it (A.6), the
 * value greyed with the note in its tooltip. `columns` names the denominator.
 */
export function RatioCell({ value, column, columns, row }) {
	if (isBlank(value)) return <NullCell column={column} />;
	const text = formatValue(column.type, value, "cell");
	const base = ratioBase(column, columns);
	const small = base ? smallBase(column, columns, row?.[base.key]) : null;
	if (!small) return text;
	return (
		<span
			className="as-ratio as-ratio--small-base"
			title={small.note}
			data-base={small.den}
		>
			{text}
		</span>
	);
}

RatioCell.propTypes = {
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	column: PropTypes.shape({
		key: PropTypes.string,
		type: PropTypes.string,
		ratio: PropTypes.array,
		minBase: PropTypes.number,
		nullHelp: PropTypes.string,
	}).isRequired,
	/** the dataset's columns, to find the denominator's label */
	columns: PropTypes.array,
	row: PropTypes.object,
};
